/**
 * Column Engine Types
 * Shared type definitions for the column system across the app
 */

// Column data types
export type ColumnType =
  | 'text'
  | 'number'
  | 'date'
  | 'boolean'
  | 'select'
  | 'multiselect'
  | 'image'
  | 'url'
  | 'currency';

// Where the column data comes from
export type ColumnSource = 'field' | 'metadata';

// Filter operators
export type FilterOperator =
  | 'eq'      // equals
  | 'neq'     // not equals
  | 'gt'      // greater than
  | 'gte'     // greater than or equal
  | 'lt'      // less than
  | 'lte'     // less than or equal
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'in'      // value in array
  | 'not_in'
  | 'between'
  | 'exists'
  | 'not_exists';

// Relative date options for date filters
export type RelativeDate =
  | 'today'
  | 'yesterday'
  | 'tomorrow'
  | 'this_week'
  | 'last_week'
  | 'next_week'
  | 'this_month'
  | 'last_month'
  | 'next_month'
  | 'this_year'
  | 'last_year'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_90_days'
  | 'last_365_days';

// Filter logic for combining multiple filters
export type FilterLogic = 'and' | 'or';

// Sort direction
export type SortDirection = 'asc' | 'desc';

// Select/dropdown option
export interface SelectOption {
  value: string;
  label: string;
}

// Individual column definition
export interface ColumnDefinition {
  key: string;
  label: string;
  type: ColumnType;
  source: ColumnSource;

  // Where to find the data
  fieldName?: string;      // Direct column name (if source='field')
  metadataKey?: string;    // JSONB path (if source='metadata')

  // Column capabilities
  sortable: boolean;
  filterable: boolean;
  editable: boolean;

  // Filter configuration
  filterOperators?: FilterOperator[];

  // Display options
  width?: number;
  options?: SelectOption[];  // For select/multiselect types

  // For numeric types stored as strings in JSONB
  numericCast?: boolean;
}

// Configuration for a content type (posts, users, media, etc.)
export interface ColumnConfig {
  objectType: string;        // 'posts', 'users', 'media', etc.
  label: string;             // Display name: 'Posts', 'Users', etc.
  columns: ColumnDefinition[];
  defaultColumns: string[];  // Keys of columns visible by default
  defaultSort: {
    column: string;
    direction: SortDirection;
  };
}

// User's column preferences (persisted)
export interface ColumnPreferences {
  visibleColumns: string[];                // Ordered list of visible column keys
  columnWidths?: Record<string, number>;   // Custom widths
  columnOrder?: string[];                  // Custom order (if different from visible)
}

// Column preset (pre-configured column set)
export interface ColumnPreset {
  id: string;
  name: string;              // "Compact", "Detailed", "Export"
  description?: string;
  columns: string[];
  isDefault?: boolean;
}

// Query request to the backend
export interface QueryRequest {
  objectType: string;
  columns?: string[];
  filters?: FilterCondition[];
  sort?: {
    column: string;
    direction: SortDirection;
  };
  pagination?: {
    page: number;
    perPage: number;
  };
  search?: string;
}

// Filter condition
export interface FilterCondition {
  id: string;           // Unique ID for React keys
  column: string;
  operator: FilterOperator;
  value: unknown;
  relativeTo?: RelativeDate;  // For date filters using relative dates
}

// Query result from the backend
export interface QueryResult {
  data: Record<string, unknown>[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

// Storage key generator
export function getPreferencesKey(siteId: string, contentType: string): string {
  return `column-prefs:${siteId}:${contentType}`;
}
